import { useQuery } from "@tanstack/react-query";

export type PublicConfig = {
  platformName: string;
  frontendUrl?: string;
  googleEnabled: boolean;
  googleClientId: string | null;
  signupMode?: "open" | "invite" | string;
  mapbox: {
    accessToken: string | null;
    configured: boolean;
    styleUrl: string;
    defaultCenter: { lat: number; lng: number };
    defaultZoom: number;
    needsPublicToken?: boolean;
    serverConfigured?: boolean;
  };
  ai: Record<string, unknown>;
  features: {
    googleSignup: boolean;
    smsOtp: boolean;
    signupMode?: string;
    testMode?: boolean;
    [key: string]: unknown;
  };
};

/** Older installs stored the previous product name. Show the current name instead. */
export function displayPlatformName(name: string | null | undefined) {
  const value = String(name || "").trim();
  if (!value || value.toLowerCase() === "anga") return "Meteo";
  return value;
}

const FALLBACK_CONFIG: PublicConfig = {
  platformName: "Meteo",
  googleEnabled: false,
  googleClientId: null,
  signupMode: "open",
  mapbox: {
    accessToken: null,
    configured: false,
    styleUrl: "mapbox://styles/mapbox/streets-v12",
    defaultCenter: { lat: -1.286389, lng: 36.817223 },
    defaultZoom: 7,
  },
  ai: {},
  features: {
    googleSignup: false,
    smsOtp: true,
    signupMode: "open",
  },
};

/** API origin including `/api/v1` — driven only by `VITE_API_URL` in .env */
export function getApiBaseUrl() {
  const fromEnv = import.meta.env["VITE_API_URL"] as string | undefined;
  if (!fromEnv?.trim()) {
    throw new Error(
      "VITE_API_URL is missing. Set it in anga-frontend/.env (e.g. http://localhost:<backend-port>/api/v1).",
    );
  }
  const raw = fromEnv.replace(/\/$/, "");
  // Enforce versioned path if someone sets only .../api
  if (/\/api$/i.test(raw)) return `${raw}/v1`;
  return raw;
}

export async function fetchPublicConfig(): Promise<PublicConfig> {
  const viteMapbox = String(import.meta.env["VITE_MAPBOX_PUBLIC_TOKEN"] || "").trim();
  const vitePk = viteMapbox.startsWith("pk.") ? viteMapbox : null;

  try {
    const response = await fetch(`${getApiBaseUrl()}/config/public`);
    if (!response.ok) {
      return {
        ...FALLBACK_CONFIG,
        mapbox: {
          ...FALLBACK_CONFIG.mapbox,
          accessToken: vitePk,
          configured: Boolean(vitePk),
        },
      };
    }
    const data = (await response.json()) as Partial<PublicConfig>;
    const apiToken = data.mapbox?.accessToken?.startsWith("pk.") ? data.mapbox.accessToken : null;
    const accessToken = apiToken || vitePk;
    return {
      ...FALLBACK_CONFIG,
      ...data,
      mapbox: {
        ...FALLBACK_CONFIG.mapbox,
        ...data.mapbox,
        accessToken,
        configured: Boolean(accessToken),
        needsPublicToken: false,
      },
      features: { ...FALLBACK_CONFIG.features, ...data.features },
      platformName: displayPlatformName(data.platformName),
      googleEnabled: Boolean(data.googleEnabled),
      signupMode:
        (data.signupMode as string | undefined) ||
        (data.features?.signupMode as string | undefined) ||
        FALLBACK_CONFIG.signupMode ||
        "open",
    };
  } catch {
    return {
      ...FALLBACK_CONFIG,
      mapbox: {
        ...FALLBACK_CONFIG.mapbox,
        accessToken: vitePk,
        configured: Boolean(vitePk),
      },
    };
  }
}

export function usePlatformConfig() {
  const query = useQuery({
    queryKey: ["public-config", "v2"],
    queryFn: fetchPublicConfig,
    staleTime: 60_000,
    retry: 1,
  });

  return {
    ...query,
    config: query.data ?? FALLBACK_CONFIG,
    platformName: displayPlatformName(query.data?.platformName),
    googleEnabled: query.data?.googleEnabled ?? false,
  };
}
